import * as Y from 'yjs';

import type { Data } from '@gitbook/slate';

import type { JSValue, YSlateData, YValue } from './utils';

/**
 * Returns true if the Yjs representation of data is equal to the Slate data.
 */
export function dataEquals(yData: YSlateData | undefined, data: Data): boolean {
    if (!yData || !yData.doc) {
        return false;
    }

    if (yData.size !== data.size) {
        return false;
    }

    for (const [key, value] of yData) {
        if (!data.has(key)) {
            return false;
        }

        if (!valueEquals(value, data.get(key))) {
            return false;
        }
    }

    return true;
}

/**
 * Returns true if the given object is equal to the Slate data.
 */
export function dataEqualsObject(object: object, data: Data): boolean {
    const keys = Object.keys(object);
    if (keys.length !== data.size) {
        return false;
    }

    for (const key of keys) {
        if (!data.has(key) || !valueEquals(object[key as keyof typeof object], data.get(key))) {
            return false;
        }
    }

    return true;
}
/**
 * Return true if the Yjs value equals the JS value.
 */
function valueEquals(lhs: YValue, rhs: JSValue) {
    if (lhs instanceof Y.Array && Array.isArray(rhs)) {
        if (lhs.length !== rhs.length) {
            return false;
        }

        for (let i = 0; i < lhs.length; i++) {
            if (!valueEquals(lhs.get(i), rhs[i])) {
                return false;
            }
        }

        return true;
    }

    if (lhs instanceof Y.Map && typeof rhs === 'object' && rhs !== null) {
        const keys = Object.keys(rhs);
        if (lhs.size !== keys.length) {
            return false;
        }

        const record = rhs as { [key: string]: JSValue };
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (!valueEquals(lhs.get(key), record[key])) {
                return false;
            }
        }

        return true;
    }

    return lhs === rhs;
}
